import { useEffect } from 'react';
import type { CornerIndex, ProceduralNameV2 } from '@cornerstone/shared';
import { useCalibrationV2Store } from '../store/calibrationV2';

// =============================================================================
// Calibration v2 키보드 shortcut — v1 useCalibrationShortcuts에서 이식.
//   - 화살표 nudge: 기본 0.001(≈1px), Shift ×10, Ctrl/⌘ ÷10 (기존 관례).
//   - ⌘Z / ⌘⇧Z undo·redo, S / ⇧S / ⌘S 저장, T 패턴 순환, Esc 선택 해제.
//   - 1/2/3 — 코너핀/메시/마스크 모드 전환.
//   - Delete/Backspace — 마스크 정점 삭제 (3점 미만 방지는 store가 처리).
//   - Tab — 코너 순환 (코너핀 모드).
// =============================================================================

const PATTERN_CYCLE: readonly ProceduralNameV2[] = [
  'none',
  'checker',
  'grid',
  'crosshair',
  'numbered',
  'outline',
];

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

export type ShortcutCallbacksV2 = {
  onSave?: () => void;
  onSaveAs?: () => void;
};

export function useCalibrationV2Shortcuts(callbacks?: ShortcutCallbacksV2): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (isEditable(e.target)) return;
      const s = useCalibrationV2Store.getState();

      // ESC — 선택 해제 / 대기 모달 닫기
      if (e.key === 'Escape') {
        if (s.pendingMeshDensity !== null) s.cancelMeshDensity();
        else s.clearSelection();
        return;
      }

      // Tab — 코너 순환 (코너핀 모드)
      if (e.key === 'Tab' && s.editorMode === 'corner') {
        e.preventDefault();
        const dir = e.shiftKey ? -1 : 1;
        const cur = s.selection?.kind === 'corner' ? s.selection.index : -1;
        const next = (((cur + dir) % 4) + 4) % 4;
        s.selectCorner(next as CornerIndex);
        return;
      }

      // Undo / Redo
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) s.redo();
        else s.undo();
        return;
      }

      // ⌘S / ⌘⇧S
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (e.shiftKey) callbacks?.onSaveAs?.();
        else callbacks?.onSave?.();
        return;
      }

      // 화살표 nudge
      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        if (!s.selection) return;
        e.preventDefault();
        const base = 0.001;
        const mult = e.shiftKey ? 10 : e.metaKey || e.ctrlKey ? 0.1 : 1;
        const step = base * mult;
        let dx = 0;
        let dy = 0;
        if (e.key === 'ArrowUp') dy = -step;
        else if (e.key === 'ArrowDown') dy = step;
        else if (e.key === 'ArrowLeft') dx = -step;
        else dx = step;
        s.nudgeSelected(dx, dy);
        return;
      }

      // Delete — 마스크 정점 삭제
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (s.selection?.kind === 'maskVertex') {
          e.preventDefault();
          s.deleteMaskVertices(s.selection.indices);
        }
        return;
      }

      // 단일 키 토글 — ⇧S 허용, 그 외 modifier는 무시
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case '1':
          s.setEditorMode('corner');
          return;
        case '2':
          s.setEditorMode('mesh');
          return;
        case '3':
          s.setEditorMode('mask');
          return;
        case 't':
        case 'T': {
          const i = PATTERN_CYCLE.indexOf(s.testPattern);
          const next = PATTERN_CYCLE[(i + 1) % PATTERN_CYCLE.length]!;
          s.setTestPattern(next);
          return;
        }
        case 'r':
        case 'R':
          s.resetToDefault();
          return;
        case 's':
          e.preventDefault();
          callbacks?.onSave?.();
          return;
        case 'S':
          e.preventDefault();
          callbacks?.onSaveAs?.();
          return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [callbacks]);
}
